import { sum, uniq } from 'ramda';
import groupContractCodes from 'constants/groupContractCodes';
import groupProductFamilies from 'constants/groupProductFamilies';
import { isUserEurostarAgent } from 'utils/booking';
import { disabilityTypes, searchTypes } from './BookingMagnetMultiJourney.constants';
import memberProductFamilies from '../../constants/memberProductFamilies';

const validateStandardRange = ({ adults12To15, adults16Plus, children5To11, children4Only }) => {
  const totalNumber = sum([adults12To15, adults16Plus, children5To11, children4Only]);
  if (totalNumber === 0 || totalNumber > 9) {
    return 'bookingMagnet.standardBookingRangeError';
  }

  return '';
};

const validateGroupRange = ({ adults, children, infants, seatedInfants, youths, seniors }) => {
  const totalNumber = sum([adults, children, youths, seniors, seatedInfants]);

  if (totalNumber === 0 || totalNumber > 99) {
    return 'numberOfPassengersError';
  }

  if (infants > adults) {
    return 'onlyOneInfantError';
  }

  return '';
};

const getRdgStations = (stations, queryFields) => {
  const { origin, destination, returningOrigin, returningDestination } = queryFields;
  const selectedStations = uniq([origin, destination, returningOrigin, returningDestination].filter(Boolean));

  return stations.filter((s) => selectedStations.includes(s.uic) && s.isRdg);
};

const getDefaultStations = (user, stations) => {
  const trimBase = (str) => str && str.replace(/^STN_/, '');

  const startingOriginCode = stations.find((s) => trimBase(user.defaultJourneyOrigin) === trimBase(s.shortCode));

  const startingDestinationCode = stations.find((s) => trimBase(user.defaultJourneyDestination) === trimBase(s.shortCode));

  return {
    origin: startingOriginCode ? startingOriginCode.uic : '',
    destination: startingDestinationCode ? startingDestinationCode.uic : ''
  };
};

const getFareTypesToDisplay = (user, selectedSearchType, isMember) => {
  const { productFamilies = [], contracts = [] } = user;

  const isGroupSearch = selectedSearchType === searchTypes.GROUP;

  if (isMember && !isGroupSearch) {
    return productFamilies.filter((ft) => memberProductFamilies.includes(ft.familyGroup));
  }

  if (isUserEurostarAgent(user)) {
    if (isGroupSearch) {
      return productFamilies.filter((ft) => groupProductFamilies.includes(ft.familyGroup));
    }

    return productFamilies.filter((ft) => !groupProductFamilies.includes(ft.familyGroup));
  }

  if (isGroupSearch) {
    return contracts.filter((ft) => groupContractCodes.includes(ft.familyGroup));
  }

  return contracts.filter((ft) => !groupContractCodes.includes(ft.familyGroup));
};

export const hasAtLeastOnePassenger = (
  {
    adults,
    adults12To15,
    adults16Plus,
    children,
    children4Only,
    children5To11,
    adultsWheelchair,
    childrenWheelchair,
    youths,
    seniors,
    guideDogs,
    wheelchairCompanions,
    nonWheelchairCompanions,
    disabilityType
  },
  user
) =>
  (disabilityType !== disabilityTypes.COMPANION_ONLY &&
    disabilityType !== disabilityTypes.ASSISTANCE_DOG_ONLY &&
    sum([
      adults,
      adults12To15,
      adults16Plus,
      children,
      children4Only,
      children5To11,
      adultsWheelchair,
      childrenWheelchair,
      youths,
      seniors
    ]) > 0) ||
  (isUserEurostarAgent(user) &&
    ((disabilityType === disabilityTypes.COMPANION_ONLY && sum([wheelchairCompanions, nonWheelchairCompanions]) > 0) ||
      (disabilityType === disabilityTypes.ASSISTANCE_DOG_ONLY && !!guideDogs)));

const isBookingValid = (formValues) => {
  const { origin, destination, outboundDate } = formValues;

  return Boolean(origin && origin.length > 0 && destination && destination.length > 0 && outboundDate && outboundDate.isValid());
};

const getMostRecentJourneyForBooking = (outbound) => {
  const activeOutboundJourneys = outbound.filter((journey) => !journey?.cancelled && journey?.id?.includes('segment_'));
  const transformSegmentIdToNumber = (id) => Number(id?.split('segment_')[1]);
  const sortedOutboundJourneys = activeOutboundJourneys.sort((a, b) => transformSegmentIdToNumber(b.id) - transformSegmentIdToNumber(a.id));

  const originCode = sortedOutboundJourneys?.[0]?.origin;
  const destinationCode = sortedOutboundJourneys?.[0]?.destination;

  return {
    origin: originCode?.uic || '',
    destination: destinationCode?.uic || ''
  };
};

export {
  isBookingValid,
  getRdgStations,
  validateGroupRange,
  validateStandardRange,
  getDefaultStations,
  getFareTypesToDisplay,
  getMostRecentJourneyForBooking
};
